import { Injectable, NgZone, inject } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { filter } from "rxjs/operators";

export interface TranscriptionProgressUpdate {
    transcriptionId: number;
    activeStep: number;
    failed: boolean;
}

export interface TranscriptionRawStatusUpdate {
    transcriptionId: number;
    rawStatus: number;
}

interface AbpNotificationEvent {
    notification?: {
        data?: { properties?: Record<string, string | number> };
        notificationName?: string;
    };
}

const TRANSCRIPTION_STATUS_NOTIFICATION = "Wapps.Transcription.Status.Notification";
const TRANSCRIPTION_SILENT_NOTIFICATION = "wapps.notification.default";
const FAILED_STATUS = 99;

/** Processing-screen step number that means SOAP is ready (status `Completed` → step 5). */
export const TRANSCRIPTION_COMPLETED_STEP = 5;

// Maps backend TranscriptionStatus enum values to processing screen step numbers
const STATUS_TO_STEP: Record<number, number> = {
    1: 3, // Processing (transcribing) → step 3
    2: 4, // PostProcessing (transcription done, SOAP generating) → step 4
    3: 5, // Completed (SOAP ready) → step 5
};

@Injectable({ providedIn: "root" })
export class TranscriptionHubService {
    private readonly _updates$ = new Subject<TranscriptionProgressUpdate>();
    private readonly _rawUpdates$ = new Subject<TranscriptionRawStatusUpdate>();
    private readonly _zone = inject(NgZone);
    private _bound = false;

    private _ensureBound(): void {
        if (this._bound) return;
        this._bound = true;

        abp.event.on("abp.notifications.received", (userNotification: AbpNotificationEvent) => {
            this._zone.run(() => this._handle(userNotification));
        });
    }

    private _handle(userNotification: AbpNotificationEvent): void {
        const notif = userNotification?.notification;
        const name = notif?.notificationName;
        if (!notif || (name !== TRANSCRIPTION_STATUS_NOTIFICATION && name !== TRANSCRIPTION_SILENT_NOTIFICATION)) return;

        const props = notif.data?.properties;
        if (!props) return;

        const transcriptionId = +props["id"];
        const status = +props["status"];

        if (!transcriptionId) return;

        if (status === FAILED_STATUS) {
            this._updates$.next({ transcriptionId, activeStep: 0, failed: true });
            this._rawUpdates$.next({ transcriptionId, rawStatus: status });
            return;
        }

        this._rawUpdates$.next({ transcriptionId, rawStatus: status });

        const activeStep = STATUS_TO_STEP[status];
        if (activeStep !== undefined) {
            this._updates$.next({ transcriptionId, activeStep, failed: false });
        }
    }

    getUpdates(transcriptionId: number): Observable<TranscriptionProgressUpdate> {
        this._ensureBound();
        return this._updates$.asObservable().pipe(filter((u) => u.transcriptionId === transcriptionId));
    }

    getAllStatusUpdates(): Observable<TranscriptionRawStatusUpdate> {
        this._ensureBound();
        return this._rawUpdates$.asObservable();
    }
}
